import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Alert,
  Stack,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { PageHeader } from '../../components/PageHeader'
import { useDevices } from '../../hooks/useDevices'
import { useGpsTrack, useSensorHistory, useSensorStats, useSensorKeys } from '../../hooks/useAnalytics'
import { subHours, formatISO, format, isValid } from 'date-fns'

// Defensively format any timestamp source. Returns '—' for null/invalid
// values instead of throwing "Invalid time value" inside MUI X charts.
function safeFmt(value: unknown, pattern: string): string {
  if (value == null) return '—'
  const d = typeof value === 'number' ? new Date(value) : new Date(value as string)
  if (!isValid(d)) return '—'
  return format(d, pattern)
}

export default function AnalyticsPage() {
  const { t } = useTranslation()
  const { data: devices = [] } = useDevices()
  const [deviceId, setDeviceId] = useState('')
  const [sensorKey, setSensorKey] = useState('')
  const [hours, setHours] = useState(24)
  const [customFrom, setCustomFrom] = useState<Date>(subHours(new Date(), 24))
  const [customTo, setCustomTo] = useState<Date>(new Date())

  const isCustom = hours === -1
  const from = useMemo(() => isCustom ? formatISO(customFrom) : formatISO(subHours(new Date(), hours)), [hours, customFrom, isCustom])
  const to = useMemo(() => isCustom ? formatISO(customTo) : formatISO(new Date()), [hours, customTo, isCustom])

  const { data: gpsPoints = [], isLoading: gpsLoading } = useGpsTrack({
    device_id: deviceId,
    from,
    to,
    limit: 500,
  })

  const { data: sensorKeys = [] } = useSensorKeys(deviceId)

  const { data: sensorHistory = [], isLoading: sensorLoading } = useSensorHistory({
    device_id: deviceId,
    sensor_key: sensorKey,
    from,
    to,
    limit: 500,
  })

  const { data: stats } = useSensorStats({
    device_id: deviceId,
    sensor_key: sensorKey,
    from,
    to,
  })

  return (
    <Box>
      <PageHeader
        title={t('analytics.title')}
        subtitle={t('analytics.subtitle')}
      />

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              select label={t('analytics.device')} value={deviceId}
              onChange={(e) => { setDeviceId(e.target.value); setSensorKey('') }}
              sx={{ minWidth: 220 }} size="small"
            >
              {devices.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select label={t('analytics.sensorKey')} value={sensorKey}
              onChange={(e) => setSensorKey(e.target.value)}
              sx={{ minWidth: 180 }} size="small"
              disabled={!deviceId || sensorKeys.length === 0}
            >
              {sensorKeys.map((k) => (
                <MenuItem key={k} value={k}>{k}</MenuItem>
              ))}
            </TextField>
            <TextField
              select label={t('analytics.timeRange')} value={hours}
              onChange={(e) => { setHours(Number(e.target.value)) }}
              sx={{ minWidth: 160 }} size="small"
            >
              <MenuItem value={1}>{t('analytics.last1h')}</MenuItem>
              <MenuItem value={6}>{t('analytics.last6h')}</MenuItem>
              <MenuItem value={12}>{t('analytics.last12h')}</MenuItem>
              <MenuItem value={24}>{t('analytics.last24h')}</MenuItem>
              <MenuItem value={48}>{t('analytics.last48h')}</MenuItem>
              <MenuItem value={168}>{t('analytics.last7d')}</MenuItem>
              <MenuItem value={-1}>{t('analytics.customRange')}</MenuItem>
            </TextField>
            {isCustom && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker label="From" value={customFrom} onChange={(v) => v && setCustomFrom(v)}
                  slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }} />
                <DateTimePicker label="To" value={customTo} onChange={(v) => v && setCustomTo(v)}
                  slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }} />
              </LocalizationProvider>
            )}
          </Box>
        </CardContent>
      </Card>

      {!deviceId && (
        <Alert severity="info">{t('analytics.selectDevice')}</Alert>
      )}

      {deviceId && gpsPoints.length > 0 && (
        <Card sx={{ mb: 3, p: 2, border: '1px solid #e2e8f0' }}>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary">GPS Points</Typography>
              <Typography variant="h6" fontWeight={700}>{gpsPoints.length}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Max Speed</Typography>
              <Typography variant="h6" fontWeight={700}>{Math.max(...gpsPoints.map(p => p.speed)).toFixed(0)} km/h</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Avg Speed</Typography>
              <Typography variant="h6" fontWeight={700}>
                {(gpsPoints.reduce((s, p) => s + p.speed, 0) / gpsPoints.length).toFixed(1)} km/h
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Time Range</Typography>
              <Typography variant="h6" fontWeight={700}>
                {gpsPoints.length > 1
                  ? safeFmt(gpsPoints[0].timestamp, 'MMM d HH:mm') + ' — ' + safeFmt(gpsPoints[gpsPoints.length - 1].timestamp, 'MMM d HH:mm')
                  : '—'}
              </Typography>
            </Box>
            {sensorKey && stats && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary">{sensorKey} Min / Avg / Max</Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {stats.min?.toFixed(1)} / {stats.avg?.toFixed(1)} / {stats.max?.toFixed(1)}
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </Card>
      )}

      {deviceId && (
        <Grid container spacing={3}>
          {/* Sensor Chart */}
          {sensorKey && (
            <>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">{t('analytics.sensorChart', { key: sensorKey })}</Typography>
                      {stats && (
                        <Stack direction="row" spacing={1}>
                          <Chip label={`Min: ${stats.min?.toFixed(2)}`} size="small" color="info" />
                          <Chip label={`Max: ${stats.max?.toFixed(2)}`} size="small" color="warning" />
                          <Chip label={`Avg: ${stats.avg?.toFixed(2)}`} size="small" color="primary" />
                          <Chip label={`${stats.count} readings`} size="small" variant="outlined" />
                        </Stack>
                      )}
                    </Box>
                    {sensorLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : sensorHistory.length === 0 ? (
                      <Alert severity="info">No sensor data in this time range</Alert>
                    ) : (
                      <LineChart
                        height={300}
                        series={[
                          {
                            data: sensorHistory.map((p) => p.value),
                            label: sensorKey,
                            color: '#34A8E0',
                            showMark: sensorHistory.length < 50,
                          },
                        ]}
                        xAxis={[
                          {
                            data: sensorHistory.map((p) => {
                              const t = p.timestamp ? new Date(p.timestamp).getTime() : NaN
                              return Number.isFinite(t) ? t : 0
                            }),
                            valueFormatter: (v: number) => safeFmt(v, 'HH:mm'),
                            scaleType: 'time',
                          },
                        ]}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          {/* GPS Speed Chart */}
          <Grid item xs={12} md={sensorKey ? 6 : 12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('analytics.speedChart')}</Typography>
                {gpsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : gpsPoints.length === 0 ? (
                  <Alert severity="info">No GPS data in this time range</Alert>
                ) : (
                  <LineChart
                    height={280}
                    series={[
                      {
                        data: gpsPoints.map((p) => p.speed),
                        label: 'Speed (km/h)',
                        color: '#388e3c',
                        showMark: false,
                        area: true,
                      },
                    ]}
                    xAxis={[
                      {
                        data: gpsPoints.map((p) => {
                          const t = p.timestamp ? new Date(p.timestamp).getTime() : NaN
                          return Number.isFinite(t) ? t : 0
                        }),
                        valueFormatter: (v: number) => safeFmt(v, 'HH:mm'),
                        scaleType: 'time',
                      },
                    ]}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* GPS Track Table */}
          {gpsPoints.length > 0 && (
            <Grid item xs={12} md={sensorKey ? 6 : 12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('analytics.gpsTrack')} ({gpsPoints.length} {t('playback.points', { count: gpsPoints.length })})
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
                    {gpsPoints.map((p, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          py: 0.5,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {safeFmt(p.timestamp, 'PPp')}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Typography variant="caption">
                            {p.latitude.toFixed(4)}, {p.longitude.toFixed(4)}
                          </Typography>
                          <Chip label={`${p.speed} km/h`} size="small" sx={{ height: 16, fontSize: 10 }} />
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  )
}
